import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { linkListStyles } from "@insite/content-library/Widgets/Basic/LinkList";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {
    extendedStyles?: FooterContainerStyles;
}

export interface FooterContainerStyles {
    widgetGridContainer?: GridContainerProps;
    logoGridItem?: GridItemProps;
    linkList1GridItem?: GridItemProps;
    linkList2GridItem?: GridItemProps;
    linkList3GridItem?: GridItemProps;
    linkList4GridItem?: GridItemProps;
    subscribeGridItem?: GridItemProps;
    socialLinksGridItem?: GridItemProps;
    siteMapLinksGridItem?: GridItemProps;
    richContentGridItem?: GridItemProps;
    logoWrapper?: InjectableCss;
    customGridWrapper?: InjectableCss;
}

linkListStyles.titleWrapper = {
    css: css`
        margin-bottom: 15px;
        font-weight: bold;
        font-size: 18px;
    `,
};

linkListStyles.linkWrapper = {
    css: css`
        padding: 5px 18px 0 0;
        ${({ theme }: { theme: BaseTheme }) =>
            breakpointMediaQueries(theme, [
                css`
                    padding: 12px 18px 0 0;
                `,
                css`
                    padding: 12px 18px 0 0;
                `,
                null,
                null,
            ])}
    `,
};

export const footerContainerStyles: FooterContainerStyles = {
    customGridWrapper: {
        css: css`
            max-width: 100%;
            background: url(/UserFiles/Images/Home/footer-pattern.png) no-repeat;
            background-size: cover;
            color: #f3f3f3;
            margin-top: 60px;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    null,
                    css`
                        // margin-top: 270px !important;
                    `,
                ])}
            border-top: ${getColor("primary.main")} 5px solid;
            h3 {
                font-family: "HelveticaCondensed";
            }
        `,
    },
    widgetGridContainer: {
        css: css`
            margin: 0 auto;
            padding: 17.5px;
            > div[class^="GridOffset"] > div[class^="GridItemStyle"] {
                div[class^="GridItemStyle"] {
                    flex-direction: column;
                    div[class^="FormInputWrapper"] {
                        margin-bottom: 10px;
                    }
                    button {
                        background: ${getColor("secondary.main")};
                        border: 2px solid ${getColor("secondary.main")};
                    }
                }
                ul {
                    margin: -10px 0 0 0;
                    padding: 0;
                    li {
                        list-style: none;
                        margin-bottom: 10px;
                        line-height: 20px;
                        a {
                            span {
                                text-decoration: underline;
                            }
                            :hover span {
                                color: ${getColor("primary.main")};
                            }
                        }
                    }
                }
                .footerMailClass {
                    span {
                        text-transform: none;
                    }
                }
                a {
                    span {
                        text-transform: capitalize;
                        color: #f3f3f3;
                    }
                    &:hover {
                        span {
                            text-decoration: underline;
                        }
                    }
                }

                span p[class^="TypographyStyle"]:first-child {
                    margin-top: -20px;
                }
                p[class^="TypographyStyle"]:first-child span {
                    font-size: 12px;
                }
            }
        `,
    },
    logoGridItem: {
        width: 12,
        css: css`
            width: 100%;
            display: flex;
            justify-content: center;
            img {
                height: 78px;
            }
        `,
    },
    linkList1GridItem: {
        width: [6, 6, 4, 3, 3],
    },
    linkList2GridItem: {
        width: [6, 6, 4, 3, 3],
    },
    linkList3GridItem: {
        width: [6, 6, 4, 3, 3],
    },
    linkList4GridItem: {
        width: [6, 6, 4, 3, 3],
    },
    subscribeGridItem: {
        width: [6, 6, 4, 3, 3],
    },
    socialLinksGridItem: {
        width: 12,
    },
    siteMapLinksGridItem: {
        width: 12,
        css: css`
            margin-bottom: 20px !important;
            padding: 10px 0;
            background: rgba(0, 0, 0, 0.2);
        `,
    },
    richContentGridItem: {
        width: 12,
    },
    logoWrapper: {
        css: css`
            width: 110px;
        `,
    },
};

const FooterContainer: FC<OwnProps> = ({ id, extendedStyles }) => {
    const [styles] = React.useState(() => mergeToNew(footerContainerStyles, extendedStyles));
    return (
        <StyledWrapper {...styles.customGridWrapper}>
            <GridContainer {...styles.widgetGridContainer}>
                <GridItem {...styles.logoGridItem}>
                    <StyledWrapper {...styles.logoWrapper}>
                        <Zone zoneName="Logo" contentId={id} />
                    </StyledWrapper>
                </GridItem>
                <GridItem {...styles.linkList1GridItem}>
                    <Zone zoneName="LinkList1" contentId={id} />
                </GridItem>
                <GridItem {...styles.linkList2GridItem}>
                    <Zone zoneName="LinkList2" contentId={id} />
                </GridItem>
                <GridItem {...styles.linkList3GridItem}>
                    <Zone zoneName="LinkList3" contentId={id} />
                </GridItem>
                <GridItem {...styles.subscribeGridItem}>
                    <Zone zoneName="Subscribe" contentId={id} />
                </GridItem>
                <GridItem {...styles.siteMapLinksGridItem}>
                    <Zone zoneName="SiteMapLinks" contentId={id} />
                </GridItem>
            </GridContainer>
        </StyledWrapper>
    );
};

const footerContainer: WidgetModule = {
    component: FooterContainer,
    definition: {
        group: "Footer",
        allowedContexts: ["Footer"],
        isSystem: true,
    },
};

export default footerContainer;
